import type { MasterEntry } from '@forq/masters-data';
import type { MachinesGateway } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import type { OrderPositionKind, OrderRecord } from '@forq/orders-data';
import { combinedDiscountPercent, type Invoice, type InvoiceLineInput, type InvoiceLineKind, type InvoiceSource } from '@forq/finance-data';

/**
 * Abrechnung eines Vorgangs — der geteilte Kern, damit die Rechnung sowohl aus dem Vorgang selbst
 * (Vorgang-Zentrierung) als auch (noch) aus der Finance-Abrechnung identisch entsteht. Reine Logik,
 * kein React: Positionsart → Rechnungszeilen-Art, Kostenstellen-Vorschlag, Positions→Zeilen-Auflösung
 * inkl. echter Warengruppe, und das Auffinden der bereits erstellten Rechnung eines Vorgangs.
 */

/** Positionsart eines Vorgangs → Rechnungszeilen-Art. */
export const lineKindForPosition: Record<OrderPositionKind, InvoiceLineKind> = {
  machine: 'machine', article: 'material', service: 'service', labor: 'labour',
  travel: 'travel', rental: 'service', transport: 'freight', text: 'text',
};

/** Sinnvolle Vorbelegung der Beleg-Kostenstelle nach Herkunft — im Entwurf änderbar. */
const costCenterCodeForSource: Partial<Record<InvoiceSource, string>> = {
  order: 'HANDEL', sales: 'HANDEL', service: 'WERKSTATT', rental: 'MIETE',
};
export function suggestCostCenterId(costCenters: readonly MasterEntry[], source: InvoiceSource): string | null {
  const code = costCenterCodeForSource[source];
  if (!code) return null;
  return costCenters.find((entry) => entry.code.toLowerCase() === code.toLowerCase())?.id ?? null;
}

/**
 * Rechnungszeilen aus den Vorgangspositionen — inkl. echter Warengruppe je Position (Maschine/Artikel/DL
 * aus dem Stamm; nachrangig, fällt bei Fehler auf die Positionsart zurück). Ohne Positionen entsteht eine
 * Pauschal-Zeile mit dem Vorgangstitel, damit die Rechnung nicht leer ist.
 *
 * RABATT: Positionsrabatt UND Auftragsrabatt des Vorgangs werden je Zeile zu EINEM Satz verrechnet.
 * Bis 09/2026 wurde beides hier stillschweigend weggelassen — die Rechnung fiel dadurch zu hoch aus,
 * der Kunde bekam einen Preis berechnet, den er nie zugesagt bekommen hatte. Die Verrechnung je Zeile
 * (statt einer Rabattzeile am Belegfuß) ist nötig, weil ein Vorgang gemischte Steuersätze tragen darf:
 * eine einzelne Rabattzeile könnte 19 % und 7 % nicht sauber trennen.
 */
export async function resolveOrderInvoiceLines(
  order: OrderRecord,
  tenantId: string,
  machinesGateway: MachinesGateway,
  masterdataGateway: MasterdataGateway,
): Promise<InvoiceLineInput[]> {
  // Je Position: Warengruppe UND -- bei einer Maschine -- ihre Besteuerungsart samt Einkaufspreis.
  // Beides kommt aus demselben Abruf, deshalb in einem Durchgang.
  const stammdaten = await Promise.all(order.positions.map(async (position) => {
    try {
      const machineId = position.machineId ?? position.sourceId;
      if (position.kind === 'machine' && machineId) {
        const machine = await machinesGateway.getMachine(tenantId, machineId);
        return {
          warengruppe: { id: machine.warengruppeId ?? null, name: machine.warengruppeName ?? '' },
          machine,
        };
      }
      if ((position.kind === 'article' || position.kind === 'service') && position.sourceId) {
        const item = await masterdataGateway.getItem(tenantId, position.sourceId);
        return { warengruppe: { id: item.warengruppeId ?? null, name: item.warengruppeName ?? '' }, machine: null };
      }
    } catch { /* Warengruppe ist nachrangig. */ }
    return null;
  }));
  const orderDiscountPercent = order.conditions.orderDiscountPercent ?? 0;
  const lines: InvoiceLineInput[] = order.positions.map((position, index) => {
    const machine = stammdaten[index]?.machine ?? null;
    // Differenzbesteuerung (§ 25a): haengt am Gegenstand, nicht am Beleg. Der Einkaufspreis wird
    // hier EINGEFROREN -- § 25a Abs. 6 verlangt die Aufzeichnung je Gegenstand, und der Preis am
    // Maschinenstamm kann sich spaeter aendern.
    const margenbesteuert = machine?.taxScheme === 'margin';
    return {
      kind: lineKindForPosition[position.kind] ?? 'other',
      description: position.description,
      quantity: position.quantity,
      unit: position.unit,
      unitPrice: position.unitPrice,
      // Textzeilen tragen keinen Betrag und deshalb auch keinen Rabatt.
      discountPercent: position.kind === 'text'
        ? 0
        : combinedDiscountPercent(position.discountPercent ?? 0, orderDiscountPercent),
      vatRate: margenbesteuert ? 0 : position.vatRate,
      taxCode: margenbesteuert ? 'D25A' : null,
      marginPurchasePrice: margenbesteuert ? (machine?.purchasePrice ?? 0) : null,
      warengruppeId: stammdaten[index]?.warengruppe.id ?? null,
      warengruppeName: stammdaten[index]?.warengruppe.name ?? '',
    };
  });
  return lines.length > 0
    ? lines
    : [{ kind: 'other', description: order.title, quantity: 1, unit: 'Pauschale', unitPrice: 0, discountPercent: 0, vatRate: 19 }];
}

/** Die (primäre) Ausgangsrechnung eines Vorgangs, falls schon erstellt (Gutschriften ausgenommen). */
export function findOrderInvoice(invoices: readonly Invoice[], orderNumber: string): Invoice | null {
  return invoices.find(
    (invoice) => invoice.sourceType === 'order' && invoice.sourceNumber === orderNumber && invoice.kind === 'invoice',
  ) ?? null;
}
